import React, { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail, resolveMediaUrl } from "@/lib/api";
import { Loader2, Save, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const FIELDS = [
  { key: "business_name", label: "Business Name" },
  { key: "tagline", label: "Tagline" },
  { key: "phone", label: "Phone (Call)" },
  { key: "sms_phone", label: "Phone (Text only)" },
  { key: "email", label: "Email" },
  { key: "address", label: "Address" },
  { key: "service_area", label: "Service Area (hero label)" },
  { key: "hours", label: "Hours" },
  { key: "hero_image_url", label: "Hero Background Image URL" },
  { key: "hero_title", label: "Hero Title" },
  { key: "hero_subtitle", label: "Hero Subtitle", textarea: true },
  { key: "facebook_url", label: "Facebook URL" },
  { key: "instagram_url", label: "Instagram URL" },
];

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"));
  }, []);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading...
      </div>
    );
  }

  const update = (key) => (e) =>
    setData((d) => ({ ...d, [key]: typeof e === "string" ? e : e.target.value }));

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData((d) => ({ ...d, logo_url: res.url }));
      toast.success("Logo uploaded. Don't forget to save.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-8 bg-cyan-400" />
          <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
            Site
          </span>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tighter">
          Business Settings
        </h1>
        <p className="text-zinc-400 mt-2">Update phone, hours, hero image, and logo.</p>
      </div>

      <form onSubmit={save} data-testid={ADMIN.settingsForm} className="space-y-6 max-w-3xl">
        {/* Logo section */}
        <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-heading text-lg uppercase tracking-tight">Logo</h3>
            {data.logo_url && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setData((d) => ({ ...d, logo_url: "" }))}
                className="rounded-none border-white/10 bg-transparent hover:border-red-400 hover:text-red-400 text-zinc-300"
              >
                <X className="w-3.5 h-3.5 mr-1.5" />
                Remove
              </Button>
            )}
          </div>
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 border border-white/10 bg-black/60 flex items-center justify-center overflow-hidden">
              {data.logo_url ? (
                <img
                  src={resolveMediaUrl(data.logo_url)}
                  alt="Logo"
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <span className="text-zinc-500 text-xs uppercase tracking-widest text-center px-2">
                  No logo
                  <br />
                  (using MSJ wordmark)
                </span>
              )}
            </div>
            <div className="flex-1">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                data-testid={ADMIN.settingsLogoUpload}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadLogo(f);
                }}
                className="hidden"
              />
              <Button
                type="button"
                disabled={uploading}
                onClick={() => logoInputRef.current?.click()}
                className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-xs h-11"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 w-4 h-4" />
                    Upload logo
                  </>
                )}
              </Button>
              <p className="text-xs text-zinc-500 mt-3">
                PNG, JPG, WEBP or SVG. Recommended: transparent PNG at ~500&times;500px.
              </p>
            </div>
          </div>
        </div>

        {/* Text fields */}
        <div className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-6 space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label className="uppercase tracking-widest text-xs text-zinc-400">
                {f.label}
              </Label>
              {f.textarea ? (
                <Textarea
                  value={data[f.key] ?? ""}
                  onChange={update(f.key)}
                  rows={3}
                  className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white mt-2"
                />
              ) : (
                <Input
                  value={data[f.key] ?? ""}
                  onChange={update(f.key)}
                  className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-11 mt-2"
                />
              )}
            </div>
          ))}
        </div>

        <Button
          type="submit"
          disabled={saving}
          data-testid={ADMIN.settingsSave}
          className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-sm h-12 px-8 shadow-[0_0_20px_rgba(0,229,255,0.3)]"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 w-4 h-4" />
              Save changes
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
